import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuthStore } from "@/auth/authStore";
import { hasGlobalRole } from "@/auth/permissions";
import type { AuthSessionContext, TokenPayload } from "@/auth/sessionTypes";
import { useLanguage } from "@/i18n/LanguageContext";

import { changePassword, fetchMyConferenceContext } from "./api";

function buildSession(payload: TokenPayload): AuthSessionContext {
  return (
    payload.session ?? {
      user: payload.user,
      global_roles: [],
      member_profile: null,
      journal_memberships: [],
    }
  );
}

export function SetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setSessionContext = useAuthStore((s) => s.setSessionContext);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }

  if (user && !user.must_change_password && !hasGlobalRole(globalRoles, "admin")) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const json = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      setTokens({
        accessToken: json.access_token,
        refreshToken: json.refresh_token,
      });
      const session = buildSession(json);
      setSessionContext(session);
      if (hasGlobalRole(session.global_roles, "admin")) {
        navigate("/admin", { replace: true });
        return;
      }
      try {
        const ctx = await fetchMyConferenceContext();
        navigate(ctx.is_reviewer && !ctx.has_registration ? "/reviews" : "/dashboard", {
          replace: true,
        });
      } catch {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 420 }}>
      <h1 style={{ fontFamily: "var(--dn-font-display)", color: "var(--dn-green-900)" }}>
        {t.setPassword.title}
      </h1>
      <p className="dn-muted">{t.setPassword.lead}</p>
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      <form className="dn-form" onSubmit={onSubmit}>
        <label>
          {t.setPassword.currentPassword}
          <input
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            type="password"
            required
            autoComplete="current-password"
          />
        </label>
        <label>
          {t.setPassword.newPassword}
          <input
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <label>
          {t.setPassword.confirmPassword}
          <input
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </label>
        <button className="dn-btn dn-btn-primary" disabled={submitting} type="submit">
          {t.setPassword.submit}
        </button>
      </form>
    </div>
  );
}
